import { defineStore } from 'pinia'
import { ref } from 'vue'
import {
  login,
  AuthType,
  type LoginByPhonePwdRequest,
  type LoginByAccountRequest,
  type LoginResponse,
} from '@/api'
import { GlobalCode, StatusCode } from '@/enums/api'
import router from '@/router'
import { getDevice, toastify } from '@/utils'
import { useChatStore } from './chat'

/**
 * 认证 Store
 * 管理登录状态、用户信息和记住密码功能
 */
export const useAuthStore = defineStore(
  'auth',
  () => {
    const isAuthenticated = ref(false)
    const token = ref<string | null>(null)
    const userInfo = ref<any>(null)

    const mobileRemember = ref(false)
    const savedMobilePhone = ref('')
    const savedMobilePassword = ref('')

    const accountRemember = ref(false)
    const savedAccountUsername = ref('')
    const savedAccountPassword = ref('')

    async function afterLoginSuccess() {
      isAuthenticated.value = true
      const chatStore = useChatStore()
      await chatStore.connectWebSocket()
    }

    async function loginByPhone(
      values: LoginByPhonePwdRequest,
      remember: boolean,
      onForceCaptcha?: (captchaNo: string) => void,
    ): Promise<void> {
      try {
        const res = await login({
          area_code: '86',
          phone: values.phone,
          password: values.password,
          type: AuthType.PhonePwd,
          device_id: getDevice(),
          captcha_no: values.captcha_no,
          captcha_param: values.captcha_param,
        })

        if (res.data?.force_captcha) {
          onForceCaptcha?.(res.data.captcha_no ?? '')
          return
        }

        if (res.code == GlobalCode.Success) {
          isAuthenticated.value = true
          const chatStore = useChatStore()
          await chatStore.connectWebSocket()
        }

        if (remember) {
          mobileRemember.value = true
          savedMobilePhone.value = values.phone
          savedMobilePassword.value = values.password
        } else {
          mobileRemember.value = false
          savedMobilePhone.value = ''
          savedMobilePassword.value = ''
        }

        const redirect = router.currentRoute.value.query.redirect
        const target =
          typeof redirect === 'string' && redirect.startsWith('/') && !redirect.startsWith('//')
            ? redirect
            : '/'
        router.replace(target)
      } catch (error: any) {
        let message = error.message || ''
        if (error.code == StatusCode.WrongPassword && error.data?.attempts) {
          message = `密码错误，您还有 ${error.data.attempts.left_attempts as number} 次尝试机会`
        }
        toastify({ type: 'danger', message })
        throw error
      }
    }

    async function loginByAccount(
      values: LoginByAccountRequest,
      remember: boolean,
      onForceCaptcha?: (captchaNo: string) => void,
    ): Promise<void> {
      try {
        const res = await login({
          type: AuthType.AccountPassword,
          account: values.account.trim(),
          password: values.password,
          device_id: getDevice(),
          captcha_no: values.captcha_no,
          captcha_param: values.captcha_param,
        })
        if (res.data?.force_captcha) {
          onForceCaptcha?.(res.data.captcha_no ?? '')
          return
        }

        if (res.code == GlobalCode.Success) {
          isAuthenticated.value = true
          const chatStore = useChatStore()
          await chatStore.connectWebSocket()
        }

        if (remember) {
          accountRemember.value = true
          savedAccountUsername.value = values.account.trim()
          savedAccountPassword.value = values.password
        } else {
          accountRemember.value = false
          savedAccountUsername.value = ''
          savedAccountPassword.value = ''
        }

        const redirect = router.currentRoute.value.query.redirect
        const target =
          typeof redirect === 'string' && redirect.startsWith('/') && !redirect.startsWith('//')
            ? redirect
            : '/'
        router.replace(target)
      } catch (e: unknown) {
        const error = e as any
        let message = error.message || ''
        if (error.code == StatusCode.WrongPassword && error.data?.attempts) {
          message = `密码错误，您还有 ${error.data.attempts.left_attempts as number} 次尝试机会`
        }
        toastify({ type: 'danger', message })
        throw e
      }
    }

    function logout() {
      isAuthenticated.value = false
      token.value = null
      userInfo.value = null
    }

    function clearMobileRemember() {
      mobileRemember.value = false
      savedMobilePhone.value = ''
      savedMobilePassword.value = ''
    }

    function clearAccountRemember() {
      accountRemember.value = false
      savedAccountUsername.value = ''
      savedAccountPassword.value = ''
    }

    function getMobileCredentials() {
      if (mobileRemember.value && savedMobilePhone.value && savedMobilePassword.value) {
        return {
          phone: savedMobilePhone.value,
          password: savedMobilePassword.value,
          remember: true,
        }
      }
      return null
    }

    function getAccountCredentials() {
      if (accountRemember.value && savedAccountUsername.value && savedAccountPassword.value) {
        return {
          account: savedAccountUsername.value,
          password: savedAccountPassword.value,
          remember: true,
        }
      }
      return null
    }

    return {
      isAuthenticated,
      token,
      userInfo,
      mobileRemember,
      savedMobilePhone,
      savedMobilePassword,
      accountRemember,
      savedAccountUsername,
      savedAccountPassword,
      loginByPhone,
      loginByAccount,
      logout,
      clearMobileRemember,
      clearAccountRemember,
      getMobileCredentials,
      getAccountCredentials,
      afterLoginSuccess,
    }
  },
  {
    persist: {
      storage: localStorage,
      pick: [
        'mobileRemember',
        'savedMobilePhone',
        'savedMobilePassword',
        'accountRemember',
        'savedAccountUsername',
        'savedAccountPassword',
        'isAuthenticated',
        'token',
        'userInfo',
      ],
    },
  },
)
